import { Injectable } from '@angular/core';
import { ApiService, GenericResponsePage } from '@uranda/commons';
import { Review, ReviewStatus } from '../app/models/review.model';
import { HttpParams } from '@angular/common/http';
import { ColumnSorting, SortingUtilsService } from '@uranda/ui';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ReviewService extends ApiService {
  sortingUtils = this.injector.get(SortingUtilsService);

  async createReview(review: Review): Promise<Review> {
    if (review.isVerified) {
      try {
        const response = await lastValueFrom(
          this.httpClient.post<any>(
            `${this.getApiUrl()}/v1/review/create`,
            review,
            {
              withCredentials: true,
            }
          )
        );
        return response.data;
      } catch (error) {
        throw error;
      }
    } else {
      const response = await this.post('/v1/review/create', review);
      return response.data;
    }
  }

  async getReviewsByPartnerId(
    partnerId?: string,
    approved?: boolean,
    verified?: boolean,
    page = 0,
    size = 100
  ): Promise<GenericResponsePage<Review[]>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (approved !== undefined) {
      params = params.set('approved', approved.toString());
    }

    if (verified !== undefined) {
      params = params.set('verified', verified.toString());
    }

    const response = await this.get(`/v1/review/${partnerId}`, params);
    return response.data;
  }

  async getAdminReviews(
    page: number,
    status?: ReviewStatus,
    size: number = 25,
    q?: string,
    currentSort?: ColumnSorting[],
    verified?: boolean
  ): Promise<GenericResponsePage<Review[]>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size)
      .set('q', q ?? '');

    if (status) {
      params = params.append('status', status);
    }

    if (verified !== undefined) {
      params = params.set('verified', verified.toString());
    }

    if (currentSort) {
      params = this.sortingUtils.appendSortingParams(params, currentSort);
    }

    const response = await this.get('/v1/admin/reviews', params);
    return response.data;
  }

  async approveReview(reviewId: number): Promise<Review> {
    const response = await this.put(`/v1/admin/reviews/${reviewId}/approve`);
    return response.data;
  }

  async rejectReview(reviewId: number): Promise<Review> {
    const response = await this.put(`/v1/admin/reviews/${reviewId}/reject`);
    return response.data;
  }

  async getPartnerReviewById(reviewId?: number): Promise<Review> {
    const response = await this.get('/v1/partner/reviews/' + reviewId);
    return response.data;
  }

  async getReviewByIdADMIN(reviewId: number): Promise<Review> {
    const response = await this.get('/v1/admin/reviews/' + reviewId);
    return response.data;
  }
  async getReviewStats(partnerId: string | number): Promise<Review> {
    const response = await this.get(`/v1/review/${partnerId}/stats`);
    return response.data;
  }
  async getPartnerReviews(
    page: number = 0,
    size: number = 25,
    q = '',
    currentSort?: ColumnSorting[],
  ): Promise<GenericResponsePage<Review[]>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('q', q);

    if (currentSort && currentSort.length !== 0) {
      params = params.append('orderBy', currentSort[0].attributeName ?? '');
      params = params.append('direction', currentSort[0].direction ?? '');
    }

    const response = await this.get('/v1/partner/reviews', params);
    return response.data;
  }

  async reportReview(reviewId?: number): Promise<Review> {
    const response = await this.post(`/v1/partner/reviews/${reviewId}/report`);
    return response.data;
  }
}
